import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { BroadmatchCell } from '../../components/BroadmatchCell/BroadmatchCell';
import { DateCell } from '../../components/DateCell/DateCell';
import { ExpandCell } from '../../components/ExpandCell/ExpandCell';
import { InlineDisplayCell } from '../../components/InlineDisplayCell/InlineDisplayCell';
import { LogCommonCell } from '../../components/LogCommonCell/LogCommonCell';
import { LoginCell } from '../../components/LoginCell/LoginCell';
import { MultipliersCell } from '../../components/MultipliersCell/MultipliersCell';
import { NetworkCell } from '../../components/NetworkCell/NetworkCell';
import { RegionsCell } from '../../components/RegionsCell/RegionsCell';
import { RestrictFrequencyCell } from '../../components/RestrictFrequencyCell/RestrictFrequencyCell';
import { SimpleMultiplierCell } from '../../components/SimpleMultiplierCell/SimpleMultiplierCell';
import { StrategyCell } from '../../components/StrategyCell/StrategyCell';
import { TimeTargetingCell } from '../../components/TimeTargetingCell/TimeTargetingCell';
import { LogLinksListCellContainer } from '../../containers/LogLinksListCellContainer/LogLinksListCellContainer';
import {
    addSettingsToDecl,
    arrangeInOrder
} from '../../service/DeclarationHelper';
import { LogHelpers } from '../../service/LogHelpers/LogHelpers';
import { maxHeightHelper } from '../../service/MaxHeightHelper';
import { ColumnsSettingsViewDict, logs } from '../../typings/common';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { GridRow } from '../../typings/domain';
import { OutputCategory } from '../../typings/graphql-log';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnStaticDecl,
    LogItemAction,
    SortDirection
} from '../../typings/grid';
import {
    BroadmatchGroups,
    DisplayGroups,
    InlineDisplayGroups,
    logBanner,
    logCampaign,
    LogEntry,
    logGroup,
    LogTypenames,
    MultipliersGroups,
    NetworkGroups,
    NullableNumber,
    NullableString,
    RegionsGroups,
    RestrictFrequencyGroups,
    SimpleMultiplierGroups,
    StrategyGroups,
    TimeTargetGroups
} from '../../typings/log';

import * as keyset from './log.i18n';

const i18n = i18nFactory(keyset);

export enum LogColumnKey {
    id = 'id',
    date = 'date',
    userName = 'userName',
    cid = 'cid',
    groupNo = 'groupNo',
    bannersNo = 'bannersNo',
    changedFieldId = 'changedFieldId',
    changedValue = 'changedValue',
    expand = 'expand'
}

function getFixedLogColumns(): Array<GridColumnStaticDecl<LogEntry>> {
    return [
        {
            title: i18n('number'),
            key: LogColumnKey.id,
            isSortable: false,
            fixed: true,
            minWidth: 80,
            CellComponent: React.memo(
                ({
                    rowData: { index }
                }: GridCellProps<LogEntry>): JSX.Element => {
                    return (
                        <LogCommonCell>{(index + 1).toString()}</LogCommonCell>
                    );
                }
            )
        },
        {
            title: i18n('date'),
            key: LogColumnKey.date,
            defaultSortDirection: SortDirection.desc,
            isSortable: true,
            fixed: true,
            mandatory: true,
            tooltipText: i18n('moscow-time'),
            tooltipKey: DocCenterColumnTooltip.moscowTime,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { date }
                }: GridCellProps<LogEntry>): JSX.Element => {
                    return <DateCell date={date} />;
                }
            )
        }
    ];
}

function getLogColumnsDecl(
    rowExpanded: Map<number, boolean>,
    rowsCanExpand: Map<number, boolean>,
    login: string,
    maxHeightCleared: boolean,
    columnsSettings: ColumnsSettingsViewDict,
    isBidCorrectionSearchEnabled?: boolean
): Array<GridColumnStaticDecl<LogEntry>> {
    return [
        {
            title: i18n('userName'),
            key: LogColumnKey.userName,
            isSortable: false,
            minWidth: 140,
            CellComponent: React.memo(
                ({
                    rowData: { userName, tool }
                }: GridCellProps<LogEntry>): JSX.Element => {
                    return <LoginCell login={userName} tool={tool} />;
                }
            )
        },
        {
            title: i18n('cid'),
            key: LogColumnKey.cid,
            isSortable: false,
            minWidth: 180,
            CellComponent: React.memo(
                ({
                    rowData: { campaign, index },
                    actions
                }: GridCellProps<LogEntry, LogItemAction>): JSX.Element => {
                    const maxHeight = maxHeightHelper.getMaxHeight(index);
                    const { calcType, id: cid } = campaign;

                    return (
                        <LogLinksListCellContainer
                            data={campaign}
                            isExpanded={false}
                            canExpand={false}
                            rowIndex={index}
                            maxHeight={maxHeight}
                            onHeightCalculated={actions.onHeightCalculated}
                            cid={cid}
                            calcType={calcType}
                            login={login}
                            maxHeightCleared={false}
                            onHeightRecalculated={actions.onHeightRecalculated}
                            type={logCampaign}
                            onNameClick={actions.onEntityClick}
                        />
                    );
                }
            )
        },
        {
            title: i18n('groupNo'),
            key: LogColumnKey.groupNo,
            isSortable: false,
            minWidth: 180,
            CellComponent: React.memo(
                ({
                    rowData: {
                        groupsNo,
                        campaign: { id, calcType },
                        index
                    },
                    actions
                }: GridCellProps<LogEntry, LogItemAction>): JSX.Element => {
                    const isExpanded = rowExpanded.get(index) || false,
                        maxHeight = maxHeightHelper.getMaxHeight(index),
                        canExpand = Boolean(rowsCanExpand.get(index));

                    return (
                        <LogLinksListCellContainer
                            data={groupsNo}
                            isExpanded={isExpanded}
                            canExpand={canExpand && groupsNo.length > 1}
                            rowIndex={index}
                            maxHeight={maxHeight}
                            onHeightCalculated={actions.onHeightCalculated}
                            cid={id}
                            calcType={calcType}
                            login={login}
                            maxHeightCleared={false}
                            onHeightRecalculated={actions.onHeightRecalculated}
                            type={logGroup}
                            onNameClick={actions.onEntityClick}
                        />
                    );
                }
            )
        },
        {
            title: i18n('bannersNo'),
            key: LogColumnKey.bannersNo,
            isSortable: false,
            minWidth: 180,
            CellComponent: React.memo(
                ({
                    rowData: {
                        bannersNo,
                        index,
                        campaign: { id, calcType }
                    },
                    actions
                }: GridCellProps<LogEntry, LogItemAction>): JSX.Element => {
                    const isExpanded = rowExpanded.get(index) || false,
                        maxHeight = maxHeightHelper.getMaxHeight(index),
                        canExpand = Boolean(rowsCanExpand.get(index));

                    return (
                        <LogLinksListCellContainer
                            data={bannersNo}
                            isExpanded={isExpanded}
                            canExpand={canExpand && bannersNo.length > 1}
                            rowIndex={index}
                            maxHeight={maxHeight}
                            onHeightCalculated={actions.onHeightCalculated}
                            cid={id}
                            calcType={calcType}
                            login={login}
                            maxHeightCleared={false}
                            onHeightRecalculated={actions.onHeightRecalculated}
                            type={logBanner}
                            onNameClick={actions.onEntityClick}
                        />
                    );
                }
            )
        },
        {
            title: i18n('changedParameter'),
            key: LogColumnKey.changedFieldId,
            isSortable: false,
            minWidth: 160,
            CellComponent: React.memo(
                ({
                    rowData: { changedFieldId }
                }: GridCellProps<LogEntry>): JSX.Element => {
                    return (
                        <LogCommonCell capitalize={true}>
                            {LogHelpers.getCategoryNameFromEnumString(
                                changedFieldId
                            )}
                        </LogCommonCell>
                    );
                }
            )
        },
        {
            title: i18n('changedValue'),
            key: LogColumnKey.changedValue,
            isSortable: false,
            minWidth: 160,
            CellComponent: React.memo(
                ({
                    rowData: {
                        changedValue,
                        category,
                        changedValueType,
                        index
                    },
                    actions
                }: GridCellProps<LogEntry, LogItemAction>): JSX.Element => {
                    const isExpanded = rowExpanded.get(index) || false,
                        rowMaxHeight = maxHeightHelper.getMaxHeight(index),
                        canExpand = Boolean(rowsCanExpand.get(index));

                    return getCell(
                        changedValueType,
                        category,
                        changedValue,
                        index,
                        actions,
                        canExpand,
                        isExpanded,
                        maxHeightCleared,
                        rowMaxHeight,
                        isBidCorrectionSearchEnabled
                    );
                }
            )
        },
        {
            title: '',
            key: LogColumnKey.expand,
            isSortable: false,
            mandatory: true,
            minWidth: 80,
            maxWidth: 80,
            CellComponent: React.memo(
                ({
                    rowData: {
                        index,
                        bannersNo,
                        groupsNo,
                        changedValue: { needExpand }
                    },
                    actions
                }: GridCellProps<LogEntry, LogItemAction>): JSX.Element => {
                    const isExpanded = rowExpanded.get(index) || false,
                        // расхлоп не нужен, если нет причины или если колонка скрыта
                        // в редьюсер не перетаскиваю, так как нет необходимости хранить это в сторе
                        noBannersExpandNeeded =
                            bannersNo.length < 2 ||
                            columnsSettings[logs].columnViewInfo.bannersNo
                                .hidden,
                        noGroupsExpandNeeded =
                            groupsNo.length < 2 ||
                            columnsSettings[logs].columnViewInfo.groupsNo
                                .hidden,
                        noChangedValueExpandedNeeded =
                            !needExpand ||
                            columnsSettings[logs].columnViewInfo.changedValue
                                .hidden;

                    if (
                        noBannersExpandNeeded &&
                        noGroupsExpandNeeded &&
                        noChangedValueExpandedNeeded
                    ) {
                        // DIRECT-80428 - возвращать null нельзя из-за того,
                        // что вместо null отображаются скелетоны (см. DIRECT-77998)
                        return <span />;
                    }

                    return (
                        <ExpandCell
                            isExpanded={isExpanded}
                            onExpandClick={(): void => {
                                actions.onExpandRow({
                                    rowIndex: index,
                                    isExpanded: !isExpanded
                                });
                            }}
                        />
                    );
                }
            )
        }
    ];
}

function getCell(
    typename: LogTypenames,
    category: OutputCategory,
    changedValue: Array<NullableNumber | NullableString> | DisplayGroups,
    index: number,
    actions: LogItemAction,
    canExpand: boolean,
    isExpanded: boolean,
    maxHeightCleared: boolean,
    maxHeight?: number,
    isBidCorrectionSearchEnabled?: boolean
): JSX.Element {
    let value;
    switch (typename) {
        case LogTypenames.regions:
        case LogTypenames.adgroupRegions:
            value = changedValue as RegionsGroups;
            return (
                <RegionsCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.timeTargeting:
            value = changedValue as TimeTargetGroups;
            return (
                <TimeTargetingCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.broadmatch:
            value = changedValue as BroadmatchGroups;
            return (
                <BroadmatchCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.restrictFrequency:
            value = changedValue as RestrictFrequencyGroups;
            return (
                <RestrictFrequencyCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.listChange:
        case LogTypenames.valueChange:
        case LogTypenames.campaignStatus:
        case LogTypenames.bannerEvent:
            value = changedValue as InlineDisplayGroups;
            return (
                <InlineDisplayCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.strategy:
            value = changedValue as StrategyGroups;
            return (
                <StrategyCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                    isBidCorrectionSearchEnabled={isBidCorrectionSearchEnabled}
                />
            );
        case LogTypenames.network:
            value = changedValue as NetworkGroups;
            return (
                <NetworkCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.simpleMultipliers:
            value = changedValue as SimpleMultiplierGroups;
            return (
                <SimpleMultiplierCell
                    diff={value}
                    type={category}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                />
            );
        case LogTypenames.demography:
        case LogTypenames.retargeting:
            value = changedValue as MultipliersGroups;
            return (
                <MultipliersCell
                    diff={value}
                    isExpanded={isExpanded}
                    canExpand={canExpand && value.needExpand}
                    rowIndex={index}
                    maxHeight={maxHeight}
                    maxHeightCleared={maxHeightCleared}
                    onHeightCalculated={actions.onHeightCalculated}
                    onHeightRecalculated={actions.onHeightRecalculated}
                    type={typename}
                />
            );
        default:
            return <LogCommonCell>{changedValue}</LogCommonCell>;
    }
}

export function getLogColumns(
    rowExpanded: Map<number, boolean>,
    rowsCanExpand: Map<number, boolean>,
    columnsSettings: ColumnsSettingsViewDict,
    login: string,
    maxHeightCleared: boolean,
    isBidCorrectionSearchEnabled?: boolean
): Array<GridColumnDecl<GridRow>> {
    const fixedColumns = getFixedLogColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, logs);
    });

    const result = getLogColumnsDecl(
        rowExpanded,
        rowsCanExpand,
        login,
        maxHeightCleared,
        columnsSettings,
        isBidCorrectionSearchEnabled
    ).map(column => {
        return addSettingsToDecl(column, columnsSettings, logs);
    });
    return fixedColumns.concat(
        arrangeInOrder(result, columnsSettings[logs].columnOrder)
    );
}
